// FLAGS: none
// Pill button showing the selected date range and toggling the range picker.
import React, { forwardRef } from "react";
import { format } from "date-fns";
import Button from "@mui/material/Button";
import EventIcon from "@mui/icons-material/Event";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Range } from "react-date-range";
import { useTranslation } from "react-i18next";

type DateFilterButtonProps = {
  isOpen: boolean;
  onClick: () => void;
  dateRangeFilter: Range[];
};

function formatDateRange(
  formattedStartDate: string | null | undefined,
  formattedEndDate: string | null | undefined,
): string {
  if (!formattedStartDate) {
    return "";
  }

  if (formattedStartDate !== formattedEndDate && formattedEndDate) {
    return `| ${formattedStartDate} – ${formattedEndDate}`;
  }

  return `| ${formattedStartDate}`;
}

const DateFilterButton = forwardRef<HTMLButtonElement, DateFilterButtonProps>(
  ({ isOpen, onClick, dateRangeFilter }, ref) => {
    const { t } = useTranslation();

    const startDate = dateRangeFilter[0]?.startDate;
    const endDate = dateRangeFilter[0]?.endDate;

    const formattedStartDate = startDate
      ? format(startDate, "dd-MM-yy")
      : undefined;
    const formattedEndDate = endDate ? format(endDate, "dd-MM-yy") : undefined;

    const hasDates = Boolean(startDate && endDate);

    return (
      <Button
        ref={ref}
        onClick={onClick}
        className={`flex items-center py-2 px-4 body2 font-medium leading-5 tracking-[0.1px] rounded-full ${
          hasDates
            ? "bg-indigo-800 text-white hover:bg-indigo-900"
            : "bg-slate-300 bg-opacity-20 text-indigo-800 hover:bg-opacity-30"
        }`}
      >
        <EventIcon className="w-5 h-5 mr-1.5" />
        {t("SessionsFilters.Date")}
        <span className="font-bold ml-1">
          {formatDateRange(formattedStartDate, formattedEndDate)}
        </span>
        {isOpen ? (
          <KeyboardArrowUpIcon className="w-5 h-5 ml-1.5" />
        ) : (
          <KeyboardArrowDownIcon className="w-5 h-5 ml-1.5" />
        )}
      </Button>
    );
  },
);

export default DateFilterButton;
